import React, { useContext } from 'react';
import HeaderNavBarContext from './context/HeaderNavBarContext';
import type { TMobileMenuItem, TMobileMenuSection, TMobileMenuPlaceAd } from '../../types/THeaderConfig';
import BasicModal from '../../components/Modal/BasicModal';
import { MenuItem } from '../../components/Menu';
import { Icon } from '../../components/Icon';
import { PrimaryButton, ButtonIcon } from '../../components/Button';
import Title5 from '../../components/Title/Title5';
import Box from '../../components/Box/Box';
import fireEvent from './utils/fireEvent';
import { TIcon } from 'types/TStyleVariables';

type TMyAccountMobileMenuProps = {
  isVisible: boolean;
  hideMenu: () => void;
  title?: string;
};

type TMobileMenuItemProps = {
  item: TMobileMenuItem;
  hideMenu: () => void;
};

type TMobileMenuSectionProps = {
  section: TMobileMenuSection;
  hideMenu: () => void;
};

type TPlaceAdButtonProps = {
  placeAd?: TMobileMenuPlaceAd;
};

const MobileMenuItem = ({ item, hideMenu }: TMobileMenuItemProps) => {
  const handleClick = () => {
    hideMenu();
  };

  return (
    <Box as="div" name="MyAccountMobileMenu-listItem">
      <MenuItem
        href={item.url}
        icon={item.iconName ? <Icon name={item.iconName as TIcon} /> : undefined}
        title={item.text}
        subtitle={item.description}
        onClick={handleClick}
        data-role={`mymp-${item.role}`}
      />
    </Box>
  );
};

const MobileMenuSection = ({ section, hideMenu }: TMobileMenuSectionProps) => {
  if (section.items.length === 0) return null;

  return (
    <Box as="div" name="MyAccountMobileMenu-section">
      {section.title && (
        <Box as="div" name="MyAccountMobileMenu-sectionHeader">
          <Title5 color="TextPrimary">{section.title}</Title5>
        </Box>
      )}
      <Box as="div" name="MyAccountMobileMenu-sectionContent">
        <ul>
          {section.items.map((item) => (
            <MobileMenuItem key={item.role} item={item} hideMenu={hideMenu} />
          ))}
        </ul>
      </Box>
    </Box>
  );
};

const PlaceAdButton = ({ placeAd }: TPlaceAdButtonProps) => {
  if (!placeAd?.url || !placeAd?.text) return null;

  const handlePlaceAd = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const placeAdEvent = fireEvent(
      'placead',
      {},
      {
        cancelable: true,
        node: event.target as HTMLElement,
      },
    );

    if (placeAdEvent.defaultPrevented) {
      event.preventDefault();
    }
  };

  return (
    <Box as="div" name="MyAccountMobileMenu-placeAd">
      <PrimaryButton as="a" href={placeAd.url} onClick={handlePlaceAd} data-role={placeAd.role}>
        <ButtonIcon name="pin" />
        {placeAd.text}
      </PrimaryButton>
    </Box>
  );
};

const MyAccountMobileMenu = ({ isVisible, hideMenu, title }: TMyAccountMobileMenuProps) => {
  const { mobileMenuConfig } = useContext(HeaderNavBarContext) ?? {};

  if (!isVisible || !mobileMenuConfig) return null;

  const { placeAd, sections } = mobileMenuConfig;

  return (
    <BasicModal title={title} onClose={hideMenu} size="full" className="hz-MyAccountMobileMenu">
      <Box as="div" name="MyAccountMobileMenu-content">
        <PlaceAdButton placeAd={placeAd} />

        {sections.map((section) => (
          <MobileMenuSection key={section.key} section={section} hideMenu={hideMenu} />
        ))}
      </Box>
    </BasicModal>
  );
};

export default MyAccountMobileMenu;
